type ThrottleOptions = {
  /**
   * 최초 호출 시 즉시 실행할지 여부 (기본값: true)
   */
  leading?: boolean;
  /**
   * 마지막 호출을 쓰로틀링 시간 이후에 실행할지 여부 (기본값: true)
   */
  trailing?: boolean;
};

type AnyFunction = (...args: any[]) => void;

/**
 * 주어진 함수의 호출을 제한하는 쓰로틀링 함수입니다.
 *
 * @template F - 함수 타입
 * @param {F} func - 쓰로틀링할 함수
 * @param {number} wait - 호출 제한 시간 (밀리초 단위)
 * @param {ThrottleOptions} [options={}] - 쓰로틀링 옵션 (`leading`, `trailing`)
 * @returns {F & { cancel: () => void }} - 쓰로틀링된 함수와 취소 메서드를 포함한 객체
 *
 * @example
 * const throttledLog = throttle((msg) => console.log(msg), 2000);
 * throttledLog("Hello"); // 즉시 실행
 * throttledLog("World"); // 2초 후 실행
 */
export function throttle<F extends AnyFunction>(
  func: F,
  wait: number,
  options: ThrottleOptions = {}
): F & { cancel: () => void } {
  let lastCallTime = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<F> | null = null;
  let lastThis: any = null;

  const { leading = true, trailing = true } = options;

  /**
   * 주어진 시간에 함수 호출을 실행합니다.
   *
   * @param {number} time - 현재 시간
   */
  function invokeFunc(time: number) {
    lastCallTime = time;
    func.apply(lastThis, lastArgs!);
    lastArgs = lastThis = null;
  }

  /**
   * 최초 호출 시 `leading` 옵션에 따라 함수를 실행합니다.
   *
   * @param {number} time - 현재 시간
   */
  function leadingEdge(time: number) {
    if (leading) {
      invokeFunc(time);
    }
    timeout = setTimeout(timerExpired, wait);
  }

  /**
   * 타이머가 만료되면 `trailing` 옵션에 따라 마지막 호출을 실행합니다.
   */
  function timerExpired() {
    const time = Date.now();
    if (trailing && lastArgs) {
      invokeFunc(time);
    }
    timeout = null;
  }

  /**
   * 쓰로틀링된 함수입니다.
   *
   * @param {...Parameters<F>} args - 함수에 전달할 인자
   */
  function throttled(this: any, ...args: Parameters<F>) {
    // const now = Date.now();
    // const remainingTime = wait - (now - lastCallTime);

    // lastArgs = args;
    // lastThis = this;

    // if (remainingTime <= 0 || remainingTime > wait) {
    //   if (timeout) {
    //     clearTimeout(timeout);
    //     timeout = null;
    //   }
    //   invokeFunc(now);
    // } else if (!timeout && trailing) {
    //   timeout = setTimeout(timerExpired, remainingTime);
    // }
    const now = Date.now();
    const remainingTime = wait - (now - lastCallTime);

    lastArgs = args;
    lastThis = this;

    if (remainingTime <= 0 || remainingTime > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      if (leading) {
        invokeFunc(now);
      } else if (trailing) {
        timeout = setTimeout(timerExpired, wait);
      }
    } else if (!timeout && trailing) {
      timeout = setTimeout(timerExpired, remainingTime);
    }
  }

  /**
   * 쓰로틀링된 함수의 실행을 취소합니다.
   */
  throttled.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
    }
    lastCallTime = 0;
    timeout = lastArgs = lastThis = null;
  };

  return throttled as F & { cancel: () => void };
}

// export function createThrottledRequest<F extends (...args: any[]) => Promise<any>>(
//   func: F,
//   wait: number,
//   options = { leading: true, trailing: false }
// ): (...args: Parameters<F>) => Promise<ReturnType<F>> {
//   let pendingPromise: Promise<ReturnType<F>> | null = null;

//   // 쓰로틀링된 함수
//   const throttledFunc = throttle(
//     (...args: Parameters<F>) => {
//       pendingPromise = func(...args);
//       return pendingPromise;
//     },
//     wait,
//     options
//   );

//   // 래퍼 함수: 쓰로틀링된 함수를 호출하고 Promise를 반환
//   return (...args: Parameters<F>): Promise<ReturnType<F>> => {
//     return new Promise((resolve, reject) => {
//       try {
//         throttledFunc(...args);
//         if (pendingPromise) {
//           pendingPromise.then(resolve).catch(reject);
//         } else {
//           reject(new Error('Throttled function did not return a promise.'));
//         }
//       } catch (error) {
//         reject(error);
//       }
//     });
//   };
// }
export function createThrottledRequest<F extends (...args: any[]) => Promise<any>>(
  func: F,
  wait: number,
  options = { leading: true, trailing: true }
): (...args: Parameters<F>) => Promise<ReturnType<F>> {
  let pendingPromise: Promise<ReturnType<F>> | null = null;

  const throttledFunc = throttle(
    async (...args: Parameters<F>) => {
      try {
        pendingPromise = func(...args);
        await pendingPromise;
      } catch (error) {
        pendingPromise = Promise.reject(error);
      }
    },
    wait,
    options
  );

  return (...args: Parameters<F>): Promise<ReturnType<F>> => {
    return new Promise((resolve, reject) => {
      throttledFunc(...args);
      if (pendingPromise) {
        pendingPromise.then(resolve).catch(reject);
      } else {
        reject(new Error('Throttled function did not return a promise.'));
      }
    });
  };
}